import { reaction } from "mobx";
import { FeatureFlag } from "healthex-common/features.dtos";
import { LoginFlow } from "../Logic/AuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { effectiveQueryParams } from "./queryParams";
import { routeEquals } from "./RoutingUIManager";
import { generateOAuthState } from "../Logic/AuthManager";
export class HostedIdpSsoManager {
    constructor(persistence, routingUIManager, studyManager, authManager, featureManager) {
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.authManager = authManager;
        this.featureManager = featureManager;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if (routeEquals(currentRoute, NamedRoutes.PATIENT_CONSENT_IDP_CALLBACK)) {
                void this.handleCallback();
            }
        }, { fireImmediately: true });
    }
    async maybeStartSso(studyId, iss) {
        if (!this.featureManager.isFeatureEnabled(FeatureFlag.HOSTED_IDP_SSO_ENABLED)) {
            console.log("[HostedFlow] maybeStartSso aborted: HOSTED_IDP_SSO_ENABLED is not enabled for this org.");
            return;
        }
        if (this.authManager.isLoggedIn && !this.authManager.isHostedSsoRestricted) {
            console.log(`[HostedFlow] Skipping hosted-SSO redirect: device already has a full-scope session; proceeding with existing token.`);
            return;
        }
        const allowed = await this.studyManager.isIdpIssuerAllowedForStudy(studyId, iss);
        if (!allowed) {
            console.log(`[HostedFlow] iss validation failed: "${iss}" is not in the allowed IdP issuer list for study ${studyId}; clearing any existing iss from sessionStorage.`);
            this.persistence.setIss(null);
            return;
        }
        console.log(`[HostedFlow] iss validation succeeded: "${iss}" is allowed for study ${studyId}; persisting to sessionStorage.`);
        this.persistence.setIss(iss);
        this.persistence.setHostedSsoStudyId(studyId);
        this.redirectToIdp(iss);
    }
    redirectToIdp(iss) {
        const state = generateOAuthState();
        this.persistence.setHostedSsoState(state);
        const callbackUrl = `${window.location.origin}/#/patient-consent/idp-callback`;
        const authorizeUrl = new URL(iss);
        authorizeUrl.searchParams.set("state", state);
        authorizeUrl.searchParams.set("redirect_uri", callbackUrl);
        console.log(`[HostedFlow] Redirecting to IdP: ${authorizeUrl.toString()}`);
        window.location.href = authorizeUrl.toString();
    }
    async handleCallback() {
        const queryParams = effectiveQueryParams();
        const code = queryParams.get("code");
        const incomingState = queryParams.get("state");
        const storedState = this.persistence.getHostedSsoState();
        const iss = this.persistence.getIss();
        const studyId = this.persistence.getHostedSsoStudyId();
        if (!code || !incomingState) {
            console.log("[HostedFlow] IdP callback missing code or state; aborting and clearing SSO state.");
            this.persistence.setHostedSsoState(null);
            this.persistence.setHostedSsoStudyId(null);
            return;
        }
        if (!storedState || incomingState !== storedState) {
            console.log("[HostedFlow] IdP callback state mismatch; aborting and clearing SSO state.");
            this.persistence.setHostedSsoState(null);
            return;
        }
        if (!iss || !studyId) {
            console.log("[HostedFlow] IdP callback missing iss or studyId from sessionStorage; aborting.");
            this.persistence.setHostedSsoState(null);
            return;
        }
        try {
            const result = await this.studyManager.exchangeHostedSsoCode(studyId, code);
            console.log(`[HostedFlow] Backend code exchange succeeded for patient ${result.patientId}.`);
            await this.authManager.handleSocialLogin(result.token, LoginFlow.GFID_HOSTED_SSO, result.isFirstTimeLogin);
            this.persistence.setHostedSsoState(null);
            this.persistence.setHostedSsoStudyId(null);
            const cleanedUrl = new URL(window.location.href);
            cleanedUrl.searchParams.delete("code");
            cleanedUrl.searchParams.delete("state");
            window.history.replaceState(null, "", cleanedUrl.toString());
            console.log(`[HostedFlow] Redirecting back to enrollment/link for study ${studyId}.`);
            this.routingUIManager.redirectTo(NamedRoutes.PATIENT_CONSENT_ENROLLMENT, { studyId, step: "link" });
        }
        catch (e) {
            console.log("[HostedFlow] Code exchange error:", e);
            this.persistence.setHostedSsoState(null);
        }
    }
}
